import React, { useState, useEffect } from "react";
import { isEmpty, debounce } from "lodash";
import { useLazyQuery } from "react-apollo";
import {
  SaveOutlined,
  RightSquareOutlined,
  CopyOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  WarningOutlined,
  CloseOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  Col,
  Row,
  Collapse,
  message,
  Alert,
  Modal,
  Tag,
  Spin,
  Tooltip,
  Typography,
  Space,
  Popover,
  Popconfirm,
} from "antd";
import { hasIsAmplifyDefaultSelection } from "../../../../../core/utils/orgSettings";
import Button from "../../../../shared/globalStyling/Button";
import moment from "moment";
import TagIntegrationsDoc from "./components/IntegrationsTabDocs";
import CartInstallation from "./components/CartInstallation";
import CartSelect from "../../../../shared/tags/cart/CartSelect";

import GET_BASIC_TAG_ACTIVITY from "../../../../../core/GraphQl/Queries/GET_BASIC_TAG_ACTIVITY";
const { Panel } = Collapse;

const CartInstructionsModal = ({
  isOpen,
  handleClick,
  cart,
  generatedTagForModal,
}) => {
  return (
    <Modal
      title={"Cart Instructions for " + cart + "."}
      visible={isOpen}
      centered
      closable={false}
      footer={
        <Button key="submit" type="primary" onClick={handleClick}>
          OK
        </Button>
      }
      width="70vw"
    >
      <CartInstallation
        cart={cart}
        tag={generatedTagForModal}
        handleCopy={tag => {
          navigator.clipboard.writeText(tag);
          message.success("Copied to clipboard");
        }}
      />
    </Modal>
  );
};

const CreateTagModal = ({
  isModalOpened,
  setIsModalOpened,
  onCreateMultiTags,
  cartProvidersData,
  isAssistanceMode,
  setIsPopoverVisibleNewTracking,
  isPopoverVisible,
  setIsPopoverVisible,
  step,
  setStep,
  assistanceTagName,
  setAssistanceTagName,
  tag,
}) => {
  const [form] = Form.useForm();

  const tagNameString = tag.map(tag => tag.name);

  // TODO: Should use reactive form instead of state
  const [cart, setCart] = useState("BUDDI");

  const handleSubmitCreate = async () => {
    setIsPopoverVisibleNewTracking(false);
    try {
      const isFormValid = await checkFormValidity();
      if (!isFormValid) return;
      setIsPopoverVisible(false);

      if (isAssistanceMode) {
        message.loading({
          content: "Creating Tag...",
          style: { marginTop: "35px" },
          duration: 0,
        });
      } else {
        message.loading("Creating Tag...", 0);
      }

      const values = form.getFieldsValue();

      setIsModalOpened(false);
      form.resetFields();
      await onCreateMultiTags(
        cart.tagPlatform,
        values.tagName,
        moment().format("LLLL")
      );

      message.destroy();
      if (isAssistanceMode) {
        message.success({
          content: "Tag Created Successfully",
          style: { marginTop: "35px" },
        });
        setTimeout(() => {
          message.info({
            content:
              "Newer tags are usually located at the bottom of the list. If you can't see your tag, scroll down.",
            style: { marginTop: "35px" },
            duration: 5,
          });
        }, 3500);
        setStep(3);
      } else {
        message.success("Tag Created Successfully");
      }
    } catch (error) {
      console.log({ error });
      setIsPopoverVisible(true);
      message.destroy();
      if (isAssistanceMode) {
        message.error({
          content: "Failed to create tag",
          style: { marginTop: "35px" },
        });
        setIsPopoverVisibleNewTracking(true);
        setStep(1);
      } else {
        message.error("Failed to create tag");
      }
    }
  };

  const checkFormValidity = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    isModalOpened ? setIsPopoverVisible(true) : setIsPopoverVisible(false);
  }, [isModalOpened, setIsPopoverVisible]);

  return (
    <Modal
      title={"Create Tag"}
      centered
      footer={null}
      visible={isModalOpened}
      onCancel={() => {
        setIsPopoverVisible(false);
        setIsModalOpened(false);
        setStep(1);
      }}
    >
      <Form
        layout="vertical"
        onFinish={handleSubmitCreate}
        form={form}
        initialValues={{
          tagPlatform: "BUDDI",
        }}
      >
        <Row type="flex" justify="space-around">
          <Col span={24}>
            {isAssistanceMode && step === 2 ? (
              <Popover
                content="Enter your desired Tag Name."
                placement="right"
                trigger="click"
                visible={isPopoverVisible}
                onVisibleChange={setIsPopoverVisible}
              >
                <Form.Item
                  label="Name"
                  name="tagName"
                  rules={[{ required: true, message: "Tag name is required!" }]}
                >
                  <Input
                    placeholder="Tag Name"
                    onChange={e => setAssistanceTagName(e.target.value)}
                  />
                </Form.Item>
              </Popover>
            ) : (
              <Form.Item
                label="Name"
                name="tagName"
                rules={[{ required: true, message: "Tag name is required!" }]}
              >
                <Input
                  placeholder="Tag Name"
                  onChange={e => setAssistanceTagName(e.target.value)}
                />
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row type="flex" justify="space-around">
          <Col span={24}>
            <Form.Item
              label=""
              name="tagPlatform"
              rules={[{ required: true, message: "Tag name is required!" }]}
            >
              {isAssistanceMode && step === 2 ? (
                <Popover
                  content="Select your desired Cart Provider."
                  placement="right"
                  // trigger="click"
                  visible={isPopoverVisible}
                  onVisibleChange={setIsPopoverVisible}
                >
                  <CartSelect
                    type="addGenerator"
                    cartProvidersData={cartProvidersData}
                    onChange={cart => {
                      // TODO: Use form values instead of state
                      setCart({ tagPlatform: cart });
                    }}
                  />
                </Popover>
              ) : (
                <CartSelect
                  type="addGenerator"
                  cartProvidersData={cartProvidersData}
                  onChange={cart => {
                    // TODO: Use form values instead of state
                    setCart({ tagPlatform: cart });
                  }}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row type="flex" justify="space-around" align="middle">
          <Col span={24}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                htmlType="submit"
                type="primary"
                disabled={
                  !assistanceTagName ||
                  tagNameString.includes(assistanceTagName)
                }
              >
                Save
              </Button>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              {tagNameString.includes(assistanceTagName) && (
                <div style={{ color: "red" }}>Tag name is already taken!</div>
              )}
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const DocumentationAlert = () => {
  const [isIntegrationDocOpened, setIsIntegrationDocOpened] = useState(false);
  const toggle = () => setIsIntegrationDocOpened(prev => !prev);

  return (
    <>
      <Col xs={18} lg={18}>
        <Alert
          style={{ padding: "8px 10px", marginTop: "7px" }}
          description={
            <>
              <h4>
                <InfoCircleOutlined
                  style={{ color: "#1890ff", marginRight: "5px" }}
                />
                Please ensure that the tags below are installed on the{" "}
                <a
                  style={{ color: "#1890ff", cursor: "pointer" }}
                  href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/header"
                  target={"_blank"}
                >
                  header section{" "}
                </a>
                on all pages of your website. Click{" "}
                <Typography.Link onClick={toggle}>here</Typography.Link> for
                more comprehensive instructions.
              </h4>
            </>
          }
        />
        <TagIntegrationsDoc open={isIntegrationDocOpened} onCancel={toggle} />
      </Col>
    </>
  );
};

const NewTrackingTagButton = ({
  setIsModalOpened,
  isAssistanceMode,
  isModalOpened,
  setIsPopoverVisibleNewTracking,
  isPopoverVisibleNewTracking,
  step,
  setStep,
}) => {
  useEffect(() => {
    isModalOpened
      ? setIsPopoverVisibleNewTracking(false)
      : setIsPopoverVisibleNewTracking(true);
  }, [isModalOpened, setIsPopoverVisibleNewTracking]);

  return (
    <Col
      xs={6}
      lg={6}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "end",
      }}
    >
      {isAssistanceMode && step === 1 ? (
        <Popover
          content='Click the "New Tracking Tag" button.'
          placement="topLeft"
          visible={isPopoverVisibleNewTracking}
        >
          <Button
            shape="default"
            icon={<SaveOutlined />}
            onClick={() => {
              setIsModalOpened(true);
              setIsPopoverVisibleNewTracking(false);
              setStep(2);
            }}
          >
            New Tracking Tag
          </Button>
        </Popover>
      ) : (
        <Button
          shape="default"
          icon={<SaveOutlined />}
          onClick={() => {
            setIsModalOpened(true);
          }}
        >
          New Tracking Tag
        </Button>
      )}
    </Col>
  );
};

const TagInfoHeader = ({
  tagName,
  isAssistanceMode,
  step,
  assistanceTagName,
}) => {
  const tagNameString = tagName.props.children.props.children[0].props.children;

  const content = (
    <div>
      <p>Congratulations! You have successfully created your new tag!</p>
      <p>Now, click it...</p>
    </div>
  );

  return isAssistanceMode &&
    step === 3 &&
    tagNameString === assistanceTagName ? (
    <Popover content={content} visible={isAssistanceMode} placement="topLeft">
      <Row>
        <Col>
          <h3>{tagName}</h3>
        </Col>
      </Row>
    </Popover>
  ) : (
    <Row>
      <Col>
        <h3>{tagName}</h3>
      </Col>
    </Row>
  );
};

const AmplifyDefaultBadge = () => {
  return (
    <Col>
      <Tag color="green">Amplify Default</Tag>
    </Col>
  );
};

const TagInfoMetrics = props => {
  const { field, value } = props;

  return (
    <Col
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <h5
        style={{
          color: "#1890ff",
          fontSize: "10",
          textAlign: "center",
          margin: "0 auto",
        }}
      >
        {value}
      </h5>
      <h5
        style={{
          color: "#8c8c8c",
          textAlign: "center",
          margin: "0 auto",
        }}
      >
        {field}
      </h5>
      {value === 0 ? (
        <CloseOutlined style={{ color: "red" }} />
      ) : (
        <CheckOutlined style={{ color: "green" }} />
      )}
    </Col>
  );
};

const TagInfo = ({
  tag,
  handleSubmitEdit,
  handleDelete,
  selectDefaultOrgTagConfig,
  amplify,
  setCurrentSelectedCart,
  setCartModalOpen,
  setGeneratedTagForModal,
  loading,
  setLoading,
  autoUpdateLoading,
  setAutoUpdateLoading,
  currentOpenedTag,
  setCurrentOpenedTag,
  isTrafficker,
  cartProvidersData,
  orgId,
  isAssistanceMode,
  setIsAssistanceMode,
  step,
  setStep,
  assistanceTagName,
  handleCollapseChange,
}) => {
  // This is the best way I could implement
  // this with a decent user experience
  // if someone can move this into the loaders & controllers
  // while achieving the same experience, please do so
  const [
    getBasicTagActivity,
    { data: status, loading: onTestTagsLoading },
  ] = useLazyQuery(GET_BASIC_TAG_ACTIVITY);

  const [tagNameString, setTagNameString] = useState();

  useEffect(() => {
    if (tag.name) {
      setTagNameString(tag.name);
    }
  }, [tag.name]);

  const appId =
    tag.eventTags && !isEmpty(tag.eventTags[0].appId[0])
      ? tag.eventTags[0].appId[0]
      : "";
  const tagId = tag.eventTags ? tag.eventTags[0].id : null;
  const [form] = Form.useForm();
  const [tagName, setTagName] = useState(!isEmpty(tag.name) ? tag.name : "");
  const [tagAppId, setTagAppId] = useState(appId);
  const [tagPlatform, setTagPlatform] = useState(
    !isEmpty(tag.cart) ? tag.cart : ""
  );

  const [getTransactions, setGetTransactions] = useState(
    tag.transactions ? tag.transactions : 0
  );
  const [getSignups, setGetSignups] = useState(tag.signups ? tag.signups : 0);
  const [getPageViews, setGetPageViews] = useState(
    tag.pageViews ? tag.pageViews : 0
  );
  const [getTagUpdateDate, setGetTagUpdateDate] = useState(
    !isEmpty(tag.tagUpdateDate) ? tag.tagUpdateDate : ""
  );

  const [open, setOpen] = useState(false);

  const generatedTag = !isEmpty(tag.tag)
    ? typeof tag.tag.generatedTag === "string"
      ? tag.tag.generatedTag
      : JSON.stringify(JSON.parse(tag.tag.generatedTag))
    : "No Generated Tag";

  const handleCopyToClipBoard = () => {
    navigator.clipboard.writeText(generatedTag);
    message.success("Copied to clipboard");
  };

  const isAppIdChanged = tagAppId !== appId;

  const handleUpdate = obj => {
    const data = {
      eventId: tag.id,
      tagPlatform,
      tagName,
      tagAppId,
      isAmplifyDefault: tag.isAmplifyDefault,
      segmentId: tag.segmentId,
      tagId,
    };

    if (obj.name === "tagName") {
      data.tagName = obj.value;
      setAutoUpdateLoading(true);
    }

    if (obj.name === "tagAppId") {
      data.tagAppId = obj.value;
      setAutoUpdateLoading(true);
    }

    if (obj.name === "cart") {
      data.tagPlatform = obj.value;
      setLoading(true);
    }

    handleSubmitEdit(data);
  };

  useEffect(() => {
    if (status && status.getBasicTagActivity) {
      setGetTransactions(status.getBasicTagActivity.transactions);
      setGetSignups(status.getBasicTagActivity.signups);
      setGetPageViews(status.getBasicTagActivity.pageViews);
      setGetTagUpdateDate(status.getBasicTagActivity.tagUpdateDate);
    }
  }, [status]);

  // this func will invoke tag update with 300ms delay
  const handleDebouncedUpdate = debounce(obj => {
    handleUpdate(obj);
  }, 1000);

  useEffect(() => {
    setTagName(tag.name);
    setTagAppId(
      tag.eventTags && !isEmpty(tag.eventTags[0].appId[0])
        ? tag.eventTags[0].appId[0]
        : ""
    );
    setTagPlatform(tag.cart);
  }, [tag]);

  const generatedTagContent = (
    <div>
      <p>This is your tag</p>
      <p>
        Copy the code and click "NEXT" for the instructions pn placing the tag
        on your website.
      </p>
    </div>
  );

  const generatedInstructionsContent = (
    <div>
      <p>Click "VIEW INSTRUCTIONS"</p>
      <p>You're just one step closer before completing this objective!</p>
      <p>Follow the instructions on placing the tag</p>
    </div>
  );

  return (
    <Spin tip="Loading..." spinning={onTestTagsLoading}>
      <Collapse
        key={tag.id}
        style={{ marginTop: "25px" }}
        onChange={handleCollapseChange}
      >
        <Panel
          style={{ marginTop: "8px" }}
          header={
            <TagInfoHeader
              tagName={
                <>
                  {
                    <Space direction="horizontal">
                      <Col>{tagName}</Col>
                      <Col>
                        {autoUpdateLoading && currentOpenedTag === tag.id && (
                          <Spin />
                        )}
                      </Col>
                    </Space>
                  }
                </>
              }
              isAssistanceMode={isAssistanceMode}
              step={step}
              assistanceTagName={assistanceTagName}
              tagNameString={tagNameString}
            />
          }
          extra={
            <Row
              gutter={16}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {tag.isAmplifyDefault && amplify && <AmplifyDefaultBadge />}
              <Col>
                <h5
                  style={{
                    color: "#8c8c8c",
                    textAlign: "center",
                    margin: "0 auto",
                  }}
                >
                  Last updated: {getTagUpdateDate}
                </h5>
              </Col>
              <TagInfoMetrics field={"Pageviews"} value={getPageViews} />
              <TagInfoMetrics field={"Transactions"} value={getTransactions} />
              <TagInfoMetrics field={"Signups"} value={getSignups} />
            </Row>
          }
        >
          <Row>
            <Col span={24}>
              <Form
                name="tags"
                layout="vertical"
                initialValues={{ tagAppId, tagPlatform }}
                autoComplete="off"
                form={form}
                onFinish={() => {
                  handleSubmitEdit({
                    tagId: tag.id,
                    tagPlatform,
                    tagName,
                    tagAppId,
                    isAmplifyDefault: tag.isAmplifyDefault,
                    segmentId: tag.segmentId,
                  });
                }}
              >
                <Row gutter={12}>
                  <Col span={4} style={{ marginRight: "25px" }}>
                    <Form.Item
                      label="Name"
                      name={"Name"}
                      rules={[{ required: true }]}
                      initialValue={tagName}
                    >
                      <Input
                        placeholder={"Name"}
                        name="appId"
                        defaultValue={tagName}
                        onChange={e => {
                          setCurrentOpenedTag(tag.id);
                          handleDebouncedUpdate({
                            name: "tagName",
                            value: e.target.value,
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4} style={{ marginRight: "25px" }}>
                    <Form.Item
                      label="App Id"
                      tooltip={
                        "This is the identifier of your tag.  Ensure that each website only has one tag installed."
                      }
                    >
                      <Input
                        name="appId"
                        suffix={
                          isAppIdChanged && (
                            <Tooltip title="Changing the App Id manually may result in data loss">
                              <WarningOutlined style={{ color: "red" }} />
                            </Tooltip>
                          )
                        }
                        defaultValue={tagAppId}
                        onChange={e => {
                          setCurrentOpenedTag(tag.id);
                          handleDebouncedUpdate({
                            name: "tagAppId",
                            value: e.target.value,
                          });
                        }}
                        disabled={!isTrafficker}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4} style={{ marginRight: "25px" }}>
                    <Form.Item
                      label={
                        <Typography>
                          {`Cart Platform`}
                          {/* <span>
                          {" "}
                          <Tag
                            style={{ borderRadius: "15rem" }}
                            onClick={() => {
                              setGeneratedTagForModal(tag.tag.generatedTag);
                              setCurrentSelectedCart(tag.cart.toUpperCase());
                              setCartModalOpen(true);
                            }}
                            color="error"
                          >
                            Install
                          </Tag>
                        </span> */}
                        </Typography>
                      }
                    >
                      <CartSelect
                        type="editGenerator"
                        cartDefaultValue={
                          tagPlatform === "none"
                            ? "none"
                            : tagPlatform === "DUTCHIEJS"
                            ? "Dutchie Iframe"
                            : tagPlatform === "DUTCHIE"
                            ? "Dutchie Subdomain"
                            : tagPlatform
                        }
                        name="environment"
                        onChange={value => {
                          handleDebouncedUpdate({ name: "cart", value });
                        }}
                        cartProvidersData={cartProvidersData}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item label="Generated Tag">
                      {isAssistanceMode &&
                      step === 4 &&
                      tagNameString === assistanceTagName ? (
                        <Popover
                          content={
                            <div>
                              <div>{generatedTagContent}</div>
                              <div>
                                <Button
                                  type="primary"
                                  onClick={() => {
                                    setStep(5);
                                  }}
                                >
                                  Next
                                </Button>
                              </div>
                            </div>
                          }
                          placement="bottom"
                          visible={true}
                        >
                          <Input
                            placeholder="<CopyOutlined />"
                            name="generatedTag"
                            suffix={
                              <CopyOutlined onClick={handleCopyToClipBoard} />
                            }
                            value={
                              !isEmpty(tag.tag)
                                ? typeof tag.tag.generatedTag === "string"
                                  ? tag.tag.generatedTag
                                  : JSON.stringify(
                                      JSON.parse(tag.tag.generatedTag)
                                    )
                                : "Please create a tag by filling up the 'INTEGRATIONS' Tab"
                            }
                          />
                        </Popover>
                      ) : (
                        <Input
                          placeholder="<CopyOutlined />"
                          name="generatedTag"
                          suffix={
                            <CopyOutlined onClick={handleCopyToClipBoard} />
                          }
                          value={
                            !isEmpty(tag.tag)
                              ? typeof tag.tag.generatedTag === "string"
                                ? tag.tag.generatedTag
                                : JSON.stringify(
                                    JSON.parse(tag.tag.generatedTag)
                                  )
                              : "Please create a tag by filling up the 'INTEGRATIONS' Tab"
                          }
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <div style={{ marginTop: "12px" }}>
                      <Col>
                        <Button
                          onClick={() => {
                            getBasicTagActivity({
                              variables: {
                                appId: tagAppId,
                                orgId: orgId || "",
                                tagId: tag.id,
                              },
                              fetchPolicy: "network-only",
                            });
                          }}
                          disabled={
                            isEmpty(tagAppId) ||
                            isEmpty(tagPlatform) ||
                            onTestTagsLoading
                          }
                          loading={onTestTagsLoading}
                          type="primary"
                          shape="default"
                          icon={<RightSquareOutlined />}
                          size="small"
                        >
                          Test Tag
                        </Button>
                        <Popconfirm
                          title={`Are you sure you want to delete ${tagName}?`}
                          visible={open}
                          onConfirm={() => {
                            const hasReports =
                              tag &&
                              tag.rawReports &&
                              tag.rawReports.length > 0;
                            handleDelete(tag.id, hasReports);
                            setOpen(false);
                          }}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            shape="default"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              if (hasIsAmplifyDefaultSelection(tag)) {
                                return message.error(
                                  "One of the selection is an Amplify Default and can't be deleted. Please update the selection and try again."
                                );
                              }
                              setOpen(true);
                            }}
                            danger
                            size="small"
                            loading={loading || autoUpdateLoading}
                          >
                            Delete
                          </Button>
                        </Popconfirm>

                        {amplify && (
                          <Button
                            shape="default"
                            onClick={() => {
                              selectDefaultOrgTagConfig({
                                eventId: tag.id,
                                appId: tagAppId,
                                tagId,
                              });
                            }}
                            size="small"
                            loading={loading || autoUpdateLoading}
                          >
                            Select as Amplify Default
                          </Button>
                        )}
                        {isAssistanceMode &&
                        step === 5 &&
                        tagNameString === assistanceTagName ? (
                          <Popover
                            content={generatedInstructionsContent}
                            placement="top"
                            visible={true}
                          >
                            <Button
                              shape="default"
                              icon={<InfoCircleOutlined />}
                              size="small"
                              onClick={() => {
                                setGeneratedTagForModal(tag.tag.generatedTag);
                                setCurrentSelectedCart(tag.cart.toUpperCase());
                                setCartModalOpen(true);
                                setStep(1);
                                setIsAssistanceMode(false);
                              }}
                              loading={loading || autoUpdateLoading}
                            >
                              View Instructions
                            </Button>
                          </Popover>
                        ) : (
                          <Button
                            shape="default"
                            icon={<InfoCircleOutlined />}
                            size="small"
                            onClick={() => {
                              setGeneratedTagForModal(tag.tag.generatedTag);
                              setCurrentSelectedCart(tag.cart.toUpperCase());
                              setCartModalOpen(true);
                            }}
                            loading={loading || autoUpdateLoading}
                          >
                            View Instructions
                          </Button>
                        )}
                      </Col>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Panel>
      </Collapse>
    </Spin>
  );
};
const TrackingTab = ({
  multipleTags,
  onCreateMultiTags,
  onEditMultiTags,
  onDeleteMultiOrgTag,
  setOrgTagConfigAmplifyDefault,
  amplify,
  cartProvider,
  orgId,
  isTrafficker,
  cartProvidersData,
  isAssistanceMode,
  setIsAssistanceMode,
}) => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [currentSelectedCart, setCurrentSelectedCart] = useState(null);
  const [generatedTagForModal, setGeneratedTagForModal] = useState(null);
  const [autoUpdateLoading, setAutoUpdateLoading] = useState(false);
  const [currentOpenedTag, setCurrentOpenedTag] = useState(null);
  const [isPopoverVisible, setIsPopoverVisible] = useState(isModalOpened);
  const [
    isPopoverVisibleNewTracking,
    setIsPopoverVisibleNewTracking,
  ] = useState(true);
  const [step, setStep] = useState(1);
  const [assistanceTagName, setAssistanceTagName] = useState(null);

  const handleCollapseChange = key => {
    if (key && (Array.isArray(key) ? key.length : true)) {
      setStep(4);
    } else {
      setStep(3);
    }
  };

  const handleSubmitEdit = async ({
    tagId,
    tagPlatform,
    tagName,
    tagAppId,
    isAmplifyDefault,
    segmentId,
    eventId,
  }) => {
    await onEditMultiTags(
      tagId,
      tagPlatform,
      tagName,
      tagAppId,
      moment().format("LLLL"),
      isAmplifyDefault,
      segmentId,
      eventId
    );
    setLoading(false);
    setAutoUpdateLoading(false);
  };

  const handleDelete = async (id, hasReports) => {
    await onDeleteMultiOrgTag(id, hasReports);
  };

  const selectDefaultOrgTagConfig = async ({ eventId, tagId, appId }) => {
    setLoading(true);
    await setOrgTagConfigAmplifyDefault({
      eventId,
      appId,
      tagId,
    });
    setLoading(false);
  };

  const handleCartModal = () => setCartModalOpen(!cartModalOpen);

  return (
    <>
      <CreateTagModal
        isModalOpened={isModalOpened}
        setIsModalOpened={setIsModalOpened}
        onCreateMultiTags={onCreateMultiTags}
        cartProvidersData={cartProvidersData}
        isAssistanceMode={isAssistanceMode}
        isPopoverVisible={isPopoverVisible}
        setIsPopoverVisible={setIsPopoverVisible}
        setIsPopoverVisibleNewTracking={setIsPopoverVisibleNewTracking}
        step={step}
        setStep={setStep}
        assistanceTagName={assistanceTagName}
        setAssistanceTagName={setAssistanceTagName}
        tag={multipleTags}
      />
      <CartInstructionsModal
        isOpen={cartModalOpen}
        handleClick={handleCartModal}
        cart={currentSelectedCart}
        generatedTagForModal={generatedTagForModal}
        isAssistanceMode={isAssistanceMode}
      />
      <Spin tip="Loading..." spinning={isLoading}>
        <Row>
          <DocumentationAlert />
          <NewTrackingTagButton
            setIsModalOpened={setIsModalOpened}
            isModalOpened={isModalOpened}
            isAssistanceMode={isAssistanceMode}
            setIsPopoverVisibleNewTracking={setIsPopoverVisibleNewTracking}
            isPopoverVisibleNewTracking={isPopoverVisibleNewTracking}
            step={step}
            setStep={setStep}
          />
        </Row>
        <Row align="middle">
          <Col xs={24} lg={24}>
            {!isEmpty(multipleTags) ? (
              multipleTags.map(tag => {
                return (
                  <TagInfo
                    orgId={orgId}
                    key={tag.id}
                    handleSubmitEdit={handleSubmitEdit}
                    handleDelete={handleDelete}
                    selectDefaultOrgTagConfig={selectDefaultOrgTagConfig}
                    tag={tag}
                    amplify={amplify}
                    setCurrentSelectedCart={setCurrentSelectedCart}
                    setCartModalOpen={setCartModalOpen}
                    setGeneratedTagForModal={setGeneratedTagForModal}
                    isLoading={isLoading}
                    setLoading={setLoading}
                    autoUpdateLoading={autoUpdateLoading}
                    setAutoUpdateLoading={setAutoUpdateLoading}
                    currentOpenedTag={currentOpenedTag}
                    setCurrentOpenedTag={setCurrentOpenedTag}
                    isTrafficker={isTrafficker}
                    cartProvidersData={cartProvidersData}
                    isAssistanceMode={isAssistanceMode}
                    setIsAssistanceMode={setIsAssistanceMode}
                    step={step}
                    setStep={setStep}
                    setAssistanceTagName={setAssistanceTagName}
                    assistanceTagName={assistanceTagName}
                    handleCollapseChange={handleCollapseChange}
                    tagName={tag.name}
                  />
                );
              })
            ) : (
              <Col>
                <p></p>
              </Col>
            )}
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export default TrackingTab;
